import {
    normalizeRoleName,
    ROLE_NAMES,
    isTrafficViewerRole,
    isLytRole,
} from '../constants/roles.constants';

const AGENCY_OWNER_ROLES = Object.freeze([
    ROLE_NAMES.TRANSIT_AGENCY_OWNER,
    ROLE_NAMES.EMERGENCY_AGENCY_OWNER,
]);

const AGENCY_ADMIN_ROLES = Object.freeze([
    ROLE_NAMES.TRANSIT_AGENCY_ADMIN,
    ROLE_NAMES.EMERGENCY_AGENCY_ADMIN,
]);

export const isAgencyOwnerRole = (role) => {
    const normalized = normalizeRoleName(role);
    return AGENCY_OWNER_ROLES.includes(normalized);
};

const isAgencyAdminRole = (role) => {
    const normalized = normalizeRoleName(role);
    return AGENCY_ADMIN_ROLES.includes(normalized);
};

/**
 * Agency owners, agency admins, and LYT users may initiate a team role change.
 * @param {string} actorRole - Logged-in user's role
 * @returns {boolean}
 */
export const canInitiateTeamRoleChange = (actorRole) => {
    const normalized = normalizeRoleName(actorRole);
    if (isLytRole(normalized)) {
        return true;
    }
    return isAgencyOwnerRole(normalized) || isAgencyAdminRole(normalized);
};

/** Roles that cannot be assigned via Team → Change Role. */
export const TEAM_ROLE_CHANGE_EXCLUDED_ROLE_NAMES = Object.freeze([
    ROLE_NAMES.TRAFFIC_VIEWER,
    ROLE_NAMES.TRANSIT_AGENCY_OWNER,
    ROLE_NAMES.EMERGENCY_AGENCY_OWNER,
]);

/**
 * Whether a role may be selected as the new role in Team → Change Role.
 * Traffic Viewer cannot be assigned when changing from another agency role.
 *
 * @param {string} roleName - Candidate new role
 * @returns {boolean}
 */
export const isRoleAssignableInTeamRoleChange = (roleName) => {
    const normalized = normalizeRoleName(roleName);
    return !TEAM_ROLE_CHANGE_EXCLUDED_ROLE_NAMES.some(
        (excluded) => normalizeRoleName(excluded) === normalized,
    );
};

/**
 * Traffic viewer team changes are jurisdiction-only — role stays traffic_viewer.
 *
 * @param {string} targetRole - Target member's current role
 * @returns {boolean}
 */
export const isTrafficViewerJurisdictionOnlyTeamChange = (targetRole) => (
    isTrafficViewerRole(targetRole)
);

/**
 * Whether the actor may change the target member's role on the agency team page.
 *
 * Rules:
 * - Traffic viewers: jurisdiction-only updates (role remains traffic_viewer).
 * - Traffic Viewer cannot be assigned as the new role (see isRoleAssignableInTeamRoleChange).
 * - LYT users may change any non-owner member (traffic viewers: jurisdictions only).
 * - Agency owners may change admins and below (not other owners).
 * - Agency admins may not change other admins or owners.
 *
 * @param {string} actorRole - Logged-in user's role
 * @param {string} targetRole - Target member's current role
 * @returns {boolean}
 */
export const canChangeTeamMemberRole = (actorRole, targetRole) => {
    if (!canInitiateTeamRoleChange(actorRole)) {
        return false;
    }

    const actor = normalizeRoleName(actorRole);
    const target = normalizeRoleName(targetRole);

    if (isAgencyOwnerRole(target)) {
        return false;
    }

    if (isLytRole(actor)) {
        return true;
    }

    if (isAgencyOwnerRole(actor)) {
        return true;
    }

    if (isAgencyAdminRole(actor)) {
        return !isAgencyAdminRole(target) && !isAgencyOwnerRole(target);
    }

    return false;
};

/**
 * Whether the actor may initiate agency ownership transfer for the target owner.
 * LYT admins and the current owner (on their own row) may transfer ownership.
 *
 * @param {Object} params
 * @param {string} params.actorRole - Logged-in user's role
 * @param {string} [params.actorEmail] - Logged-in user's email
 * @param {string} params.targetRole - Target member's current role
 * @param {string} [params.targetEmail] - Target member's email
 * @returns {boolean}
 */
export const canTransferAgencyOwnership = ({
    actorRole,
    actorEmail,
    targetRole,
    targetEmail,
}) => {
    if (!isAgencyOwnerRole(targetRole)) {
        return false;
    }

    const actor = normalizeRoleName(actorRole);

    if (actor === ROLE_NAMES.LYT_ADMIN) {
        return true;
    }

    if (!isAgencyOwnerRole(actor) || !actorEmail || !targetEmail) {
        return false;
    }

    return actorEmail.trim().toLowerCase() === targetEmail.trim().toLowerCase();
};
